// =============================================================================
// staticSite/migrationRunner — orkiestracja krokow zakladki „Migracja".
//
// Faza 1: ensureStaticSite (wiersz draft), runAnalysis (zloz + render + bramki
// + snapshot do DB), (runPreview — podglad w iframe — usuniety 2026-09-22; previewHtml zostaje dla ParityReportTable),
// redirecty (lista / dodaj / usun). Kazdy krok to osobny przycisk w UI —
// nic nie dzieje sie automatycznie. Deploy / cutover / rollback = fazy 2-3.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import { buildFaviconBundle } from "@/lib/faviconBuilder";
import type { ZipEntry } from "@/lib/zipStore";
import type {
  ParityReport, ProgressCallback, RenderedFile, SiteModel, StaticRedirectRow, StaticSiteRow, StaticSiteSettings,
} from "./types";
import { DEFAULT_SITE_SETTINGS } from "./types";
import { assembleSite } from "./siteAssembler";
import { collectAssets, buildAssetMap, resolveAssets } from "./assetCollector";
import { makeAssetFetcher } from "./assetFetchClient";
import { renderSite, type RenderResult } from "./pageRenderer";
import { runParity, outboundLinksOf } from "./parityChecker";
import { buildAutoRedirects, mergeRedirects, redirectsFromWpRedirects } from "./redirectsBuilder";
import { domainFromUrl } from "./urlPolicy";
import { seedForDomain } from "./themes/variation";
import { createPagesProject, getPagesProject } from "./cfPagesApi";
import { deploySite } from "./cfPagesDeployer";
import { projectNameFor, type CfAccountLike } from "./cfAccountAllocator";

const PREFIX = "STATIC";

export interface AnalysisResult {
  site: SiteModel;
  report: ParityReport;
  render: RenderResult;
  redirects: StaticRedirectRow[];
  files: RenderedFile[];
}

// -----------------------------------------------------------------------------
// static_sites — wiersz per website
// -----------------------------------------------------------------------------

export async function getStaticSiteByMap(mapId: string): Promise<StaticSiteRow | null> {
  const { data, error } = await (supabase as any).from("static_sites").select("*").eq("map_id", mapId).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as StaticSiteRow) || null;
}

/** Studio /strony/:websiteId — strona sklada sie per domena (website_id UNIQUE), nie per mapa. */
export async function getStaticSiteByWebsite(websiteId: string): Promise<StaticSiteRow | null> {
  const { data, error } = await (supabase as any).from("static_sites").select("*").eq("website_id", websiteId).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as StaticSiteRow) || null;
}

export interface SitePageSnapshot {
  kind: string;
  url_path: string;
  title: string | null;
}

/** Snapshot stron z ostatniej analizy (static_site_pages) — karta Menu w Studio czyta z tego, bez assembleSite. */
export async function listSitePages(siteId: string): Promise<SitePageSnapshot[]> {
  const { data, error } = await (supabase as any).from("static_site_pages").select("kind, url_path, title").eq("site_id", siteId).limit(5000);
  if (error) throw new Error(error.message);
  return (data || []) as SitePageSnapshot[];
}

/** Lista /strony — wszystkie migracje (RLS: allowed_users + workspace). */
export async function listStaticSites(): Promise<StaticSiteRow[]> {
  const { data, error } = await (supabase as any).from("static_sites").select("*").order("updated_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data || []) as StaticSiteRow[];
}

/** Tworzy wiersz draft (1 per website), jesli go nie ma. */
export async function ensureStaticSite(input: {
  mapId: string;
  websiteId: string;
  websiteUrl: string;
  workspace?: "firma" | "sideprojects" | null;
}): Promise<StaticSiteRow> {
  const existing = await getStaticSiteByMap(input.mapId);
  if (existing) return existing;

  // Ta sama domena moze byc juz zmigrowana z INNEJ mapy — website_id UNIQUE
  const { data: byWebsite } = await (supabase as any).from("static_sites").select("*").eq("website_id", input.websiteId).maybeSingle();
  if (byWebsite) {
    throw new Error(`Ta domena ma już migrację podpiętą pod inną mapę (${byWebsite.map_id}). Jedna domena = jedna strona statyczna.`);
  }

  const domain = domainFromUrl(input.websiteUrl);
  if (!domain) throw new Error("Nie udało się odczytać domeny z adresu strony");
  const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
  const row = {
    user_id: userId,
    website_id: input.websiteId,
    map_id: input.mapId,
    workspace: input.workspace || "firma",
    domain,
    status: "draft",
    theme_id: "editorial",
    theme_seed: seedForDomain(domain),
    settings: DEFAULT_SITE_SETTINGS,
  };
  const { data, error } = await (supabase as any).from("static_sites").insert(row).select("*").single();
  if (error) throw new Error(error.message);
  logPublic(PREFIX, `Utworzono szkic migracji dla ${domain}`);
  return data as StaticSiteRow;
}

export async function updateStaticSite(siteId: string, patch: Partial<StaticSiteRow>): Promise<void> {
  const { error } = await (supabase as any)
    .from("static_sites")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", siteId);
  if (error) throw new Error(error.message);
}

export async function updateSettings(site: StaticSiteRow, patch: Partial<StaticSiteSettings>): Promise<StaticSiteSettings> {
  const next: StaticSiteSettings = { ...DEFAULT_SITE_SETTINGS, ...(site.settings || {}), ...patch };
  await updateStaticSite(site.id, { settings: next });
  return next;
}

// -----------------------------------------------------------------------------
// Redirecty
// -----------------------------------------------------------------------------

export async function listRedirects(siteId: string): Promise<StaticRedirectRow[]> {
  const { data, error } = await (supabase as any).from("static_redirects").select("*").eq("site_id", siteId).order("created_at");
  if (error) throw new Error(error.message);
  return (data || []) as StaticRedirectRow[];
}

export async function addRedirect(siteId: string, row: Omit<StaticRedirectRow, "id" | "site_id">): Promise<void> {
  const { error } = await (supabase as any)
    .from("static_redirects")
    .upsert({ site_id: siteId, ...row }, { onConflict: "site_id,from_path" });
  if (error) throw new Error(error.message);
}

export async function removeRedirect(id: string): Promise<void> {
  const { error } = await (supabase as any).from("static_redirects").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/** Wszystkie reguly: auto + wp_redirects (applied) + zapisane w DB (manual / sitemap_gap). */
export async function buildAllRedirects(site: StaticSiteRow, model: SiteModel): Promise<StaticRedirectRow[]> {
  const [dbRows, wpRows] = await Promise.all([
    listRedirects(site.id),
    (supabase as any)
      .from("wp_redirects")
      .select("from_url, to_wp_post_url, to_slug_planned, status_code, status")
      .eq("website_id", site.website_id)
      .eq("status", "applied")
      .then((r: any) => (r.data || []) as any[])
      .catch(() => [] as any[]),
  ]);
  return mergeRedirects(buildAutoRedirects(model), redirectsFromWpRedirects(wpRows, model), dbRows);
}

// -----------------------------------------------------------------------------
// Analiza (parity report) — zero wywolan Cloudflare
// -----------------------------------------------------------------------------

export async function runAnalysis(site: StaticSiteRow, onProgress?: ProgressCallback): Promise<AnalysisResult> {
  const progress = onProgress || (() => {});
  const settings = { ...DEFAULT_SITE_SETTINGS, ...(site.settings || {}) };
  try {
    const model = await assembleSite(site.map_id, { settings, fetchWpSitemap: true, fetchWpRest: true, onProgress: progress });
    for (const w of model.warnings) progress({ type: "warning", message: w });

    progress({ type: "phase", phase: "render", message: "Renderuję stronę (motyw + wariacja)" });
    const redirects = await buildAllRedirects(site, model);
    const assetMap = buildAssetMap(collectAssets(model));
    const render = renderSite(model, { themeId: site.theme_id, themeSeed: site.theme_seed, assetMap, redirects, faviconFiles: null });
    for (const w of render.warnings) progress({ type: "warning", message: w });

    progress({ type: "phase", phase: "parity", message: "Sprawdzam bramki (linki, adresy, meta, schema)" });
    const report = runParity({ site: model, files: render.files, redirects });

    // Snapshot do DB
    await persistAnalysis(site, model, report);
    progress({ type: "done", message: report.blocking ? "Analiza zakończona — są blokady" : "Analiza zakończona — bez blokad" });
    logPublic(PREFIX, `Analiza ${model.domain}: ${report.summary.pages} stron, ${report.summary.hardFailures} blokad, ${report.summary.missingUrls} brakujących URL`);
    return { site: model, report, render, redirects, files: render.files };
  } catch (e) {
    logError(PREFIX, "Analiza nieudana", e);
    progress({ type: "error", message: e instanceof Error ? e.message : String(e) });
    throw e;
  }
}

export async function persistAnalysis(site: StaticSiteRow, model: SiteModel, report: ParityReport): Promise<void> {
  const byPath = new Map(report.pages.map((p) => [p.path, p]));
  const rows = model.pages
    .filter((p) => p.kind !== "not_found")
    .map((p) => ({
      site_id: site.id,
      kind: p.kind,
      source_table: p.sourceTable,
      source_id: p.sourceId,
      url_path: p.path,
      source_url: p.sourceUrl,
      title: p.title,
      html_sha256: null,
      outbound_links: outboundLinksOf(p, model),
      meta: { seo_title: p.seoTitle, meta_description: p.metaDescription, has_schema: Boolean(p.schemaGraph) },
      parity: byPath.get(p.path) || {},
    }));
  // Deduplikacja po url_path (UNIQUE)
  const seen = new Set<string>();
  const unique = rows.filter((r) => (seen.has(r.url_path) ? false : (seen.add(r.url_path), true)));

  await (supabase as any).from("static_site_pages").delete().eq("site_id", site.id);
  for (let i = 0; i < unique.length; i += 200) {
    const { error } = await (supabase as any).from("static_site_pages").insert(unique.slice(i, i + 200));
    if (error) throw new Error(`Zapis snapshotu stron: ${error.message}`);
  }
  await updateStaticSite(site.id, {
    status: site.status === "draft" ? "analyzed" : site.status,
    last_parity: report,
    analyzed_at: new Date().toISOString(),
  });
}

// -----------------------------------------------------------------------------
// Deploy na Cloudflare Pages (<projekt>.pages.dev) — domena NIE jest ruszana
// -----------------------------------------------------------------------------

export interface DeployArgs {
  account: CfAccountLike;
  /** CF account id (32 hex) — z allocatora */
  accountId: string;
  trigger?: "manual" | "publish_batch" | "heal";
  onProgress?: ProgressCallback;
}

export interface DeployResult {
  url: string;
  subdomain: string;
  project: string;
  deployId: string;
  fileCount: number;
  uploadedCount: number;
  bytesTotal: number;
  warnings: string[];
  assetsFailed: number;
}

export async function runDeploy(site: StaticSiteRow, args: DeployArgs): Promise<DeployResult> {
  const progress = args.onProgress || (() => {});
  const settings = { ...DEFAULT_SITE_SETTINGS, ...(site.settings || {}) };
  const warnings: string[] = [];

  // 1. Zloz + redirecty
  const model = await assembleSite(site.map_id, { settings, fetchWpSitemap: true, fetchWpRest: true, onProgress: progress });
  warnings.push(...model.warnings);
  const redirects = await buildAllRedirects(site, model);

  // 2. Obrazy: pobierz bajty przez Edge Fn (te sama sciezka co na WP)
  progress({ type: "phase", phase: "assets", message: "Pobieram obrazy ze strony WP" });
  const refs = collectAssets(model);
  const resolved = await resolveAssets(refs, makeAssetFetcher(site.website_id), (cur, total, path) =>
    progress({ type: "progress", phase: "assets", current: cur, total, message: path }),
  );
  const assetMap = new Map<string, string>();
  for (const f of resolved.files) assetMap.set(f.src, f.path);
  if (resolved.failed.length) {
    warnings.push(`${resolved.failed.length} obrazów nie udało się pobrać — na stronie zostają ich oryginalne adresy (działają, póki WP żyje). Pierwszy błąd: ${resolved.failed[0].error}`);
  }

  // 3. Favicony (canvas) + render
  let faviconFiles: ZipEntry[] | null = null;
  if (model.brand?.logoSvg && typeof document !== "undefined") {
    try { faviconFiles = await buildFaviconBundle(model.brand.logoSvg, model.brand.themeColor); } catch (e) {
      warnings.push(`Favicony pominięte: ${e instanceof Error ? e.message : String(e)}`);
    }
  }
  progress({ type: "phase", phase: "render", message: "Renderuję stronę" });
  const render = renderSite(model, { themeId: site.theme_id, themeSeed: site.theme_seed, assetMap, redirects, faviconFiles });
  warnings.push(...render.warnings);

  // 4. Bramki — blokada = brak deployu
  progress({ type: "phase", phase: "parity", message: "Sprawdzam bramki przed wysyłką" });
  const report = runParity({ site: model, files: render.files, redirects });
  await persistAnalysis(site, model, report);
  if (report.blocking) {
    const failed = report.gates.filter((g) => g.level === "hard" && !g.passed).map((g) => g.id).join(", ");
    throw new Error(`Deploy zablokowany przez bramki: ${failed}. Napraw je w kroku Analiza.`);
  }

  // 5. Projekt Pages (utworz, jesli nie ma)
  progress({ type: "phase", phase: "upload", message: "Sprawdzam projekt Cloudflare Pages" });
  const projectName = site.pages_project_name || projectNameFor(site.domain);
  let project = await getPagesProject(args.account.api_token, args.accountId, projectName);
  if (!project) {
    project = await createPagesProject(args.account.api_token, args.accountId, projectName, "main");
    logPublic(PREFIX, `Utworzono projekt Pages ${projectName} na koncie ${args.account.label}`);
  }
  const subdomain = project.subdomain || `${projectName}.pages.dev`;
  await updateStaticSite(site.id, {
    pages_cf_account_id: args.account.id,
    pages_cf_account_remote_id: args.accountId,
    pages_project_name: projectName,
    pages_subdomain: subdomain,
  });

  // 6. Wiersz deployu + wysylka
  const { data: deployRow, error: insErr } = await (supabase as any)
    .from("static_deploys")
    .insert({ site_id: site.id, branch: "main", trigger: args.trigger || "manual", status: "uploading", parity_report: report.summary })
    .select("id")
    .single();
  if (insErr) throw new Error(`static_deploys: ${insErr.message}`);
  const deployId = deployRow.id as string;

  const allFiles = [...render.files, ...resolved.files.map(({ src: _src, ...f }) => f)];
  try {
    const res = await deploySite({
      token: args.account.api_token,
      accountId: args.accountId,
      project: projectName,
      branch: "main",
      files: allFiles,
      onProgress: (ev) => {
        if (ev.stage === "upload" && ev.total != null) progress({ type: "progress", phase: "upload", current: ev.current || 0, total: ev.total, message: ev.message });
        else progress({ type: "phase", phase: ev.stage === "poll" || ev.stage === "deploy" ? "deploy" : "upload", message: ev.message });
      },
    });

    const manifest: Record<string, unknown> = { ...(site.asset_manifest || {}) };
    for (const f of resolved.files) manifest[f.path] = { hash: res.manifest[f.path], bytes: f.bytes.byteLength, contentType: f.contentType, src: f.src };
    const url = `https://${subdomain}`;
    await (supabase as any).from("static_deploys").update({
      status: "success",
      cf_deployment_id: res.deployment.id,
      cf_url: res.deployment.url || url,
      file_count: res.fileCount,
      bytes_total: res.bytesTotal,
      uploaded_count: res.uploadedCount,
      finished_at: new Date().toISOString(),
    }).eq("id", deployId);
    await updateStaticSite(site.id, {
      status: site.status === "live" ? "live" : "deployed",
      last_deploy_id: deployId,
      asset_manifest: manifest as StaticSiteRow["asset_manifest"],
      needs_redeploy: false,
    });
    progress({ type: "done", message: `Wdrożone: ${url}` });
    logPublic(PREFIX, `Deploy ${site.domain} -> ${url}: ${res.fileCount} plików, wgrano ${res.uploadedCount}`);
    return { url, subdomain, project: projectName, deployId, fileCount: res.fileCount, uploadedCount: res.uploadedCount, bytesTotal: res.bytesTotal, warnings, assetsFailed: resolved.failed.length };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    await (supabase as any).from("static_deploys").update({ status: "failed", error: msg, finished_at: new Date().toISOString() }).eq("id", deployId);
    logError(PREFIX, "Deploy nieudany", e);
    progress({ type: "error", message: msg });
    throw e;
  }
}

export interface DeployHistoryRow {
  id: string;
  branch: string;
  trigger: string;
  status: string;
  cf_url: string | null;
  file_count: number | null;
  uploaded_count: number | null;
  bytes_total: number | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  /** Log builda Astro (build-worker); null dla silnika browser */
  build_log: string | null;
}

export async function listDeploys(siteId: string, limit = 8): Promise<DeployHistoryRow[]> {
  const { data, error } = await (supabase as any)
    .from("static_deploys")
    .select("id, branch, trigger, status, cf_url, file_count, uploaded_count, bytes_total, error, started_at, finished_at, build_log")
    .eq("site_id", siteId)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data || []) as DeployHistoryRow[];
}

/**
 * HTML do <iframe srcdoc>: <base href> na domenę (obrazy z WP) + CSS motywu
 * WKLEJONY inline (arkusz /assets/*.css nie istnieje jeszcze na WP, więc <link>
 * ładowałby 404 i podgląd byłby bez stylów).
 */
export function previewHtml(file: RenderedFile, baseUrl: string, allFiles: RenderedFile[] = []): string {
  let html = file.text ?? new TextDecoder().decode(file.bytes);
  const base = `<base href="${baseUrl.replace(/\/$/, "")}/">`;
  html = html.replace(/<link rel="stylesheet" href="([^"]+)">/g, (m, href: string) => {
    const css = allFiles.find((f) => f.path === href);
    return css ? `<style>${css.text ?? new TextDecoder().decode(css.bytes)}</style>` : m;
  });
  return html.includes("<head>") ? html.replace("<head>", `<head>\n${base}`) : `${base}\n${html}`;
}
